/**
 * OAuth Callback Page
 * 
 * Handles OAuth redirects from Azure AD and Google SSO.
 * Extracts tokens from URL query parameters and stores them in auth store.
 */

import React, { useEffect, useState } from 'react';
import { useAuthStore } from '../stores/auth';
import { getAPIClient } from '../api/client';
import { getCurrentUserProfile } from '../api/users';
import { clearMockRoleOnLogout } from '../hooks/useRoleSwitch';
import { PageContainer } from '../components/UI/PageContainer';
import { Card } from '../components/UI/Card';
import { Button } from '../components/UI/Button';
import { AlertCircle, Loader, ArrowLeft } from 'lucide-react';

export function OAuthCallback() {
  const { setAccessToken, setUser } = useAuthStore();
  const [showError, setShowError] = useState(false);
  
  // Extract query parameters from URL
  const urlParams = new URLSearchParams(window.location.search);
  const token = urlParams.get('token');
  const refreshToken = urlParams.get('refresh_token');
  const error = urlParams.get('error');
  const errorDescription = urlParams.get('error_description');

  useEffect(() => {
    const handleCallback = async () => {
      // Handle error case
      if (error) {
        console.error('[OAuth] Error:', error, errorDescription);
        // Show error UI instead of immediately redirecting
        setShowError(true);
        return;
      }

      // Handle success case
      if (token) {
        try {
          const client = getAPIClient();
          client.setAccessToken(token);
          // Single store update (refresh is optional; store has no separate setRefreshToken — calling undefined threw when Azure sent refresh_token)
          setAccessToken(token, refreshToken ?? undefined);
          clearMockRoleOnLogout();

          // Fetch user profile (TraceOS exposes GET /users/me, not /api/users/me)
          try {
            const userData = await getCurrentUserProfile();
            const primaryTenant =
              userData.tenant_assignments?.find((t) => t.status === 'accepted')?.tenant_id
              ?? userData.tenant_assignments?.[0]?.tenant_id;
            setUser({
              id: userData.id,
              email: userData.email,
              username: userData.username,
              full_name: userData.full_name,
              is_email_verified: userData.is_email_verified,
              tenant_id: primaryTenant,
            });
          } catch (userError) {
            console.warn('[OAuth] Could not fetch user info:', userError);
            // Continue anyway - user can still use the app
          }

          // Brief delay so zustand persist can write tracekit-auth before full reload (otherwise `/` may apply dev master key and clear SSO)
          await new Promise((r) => setTimeout(r, 50));
          window.location.href = '/';
        } catch (err) {
          console.error('[OAuth] Failed to process callback:', err);
          window.location.href = '/login?error=' + encodeURIComponent('Failed to complete authentication');
        }
      } else {
        // No token provided
        window.location.href = '/login?error=' + encodeURIComponent('No authentication token received');
      }
    };

    handleCallback();
  }, [token, refreshToken, error, errorDescription, setAccessToken, setUser]);

  // Handle navigation back to login
  const handleBackToLogin = () => {
    // Clear any error parameters from URL
    const baseUrl = window.location.pathname;
    window.history.replaceState({}, '', baseUrl);
    window.location.href = '/';
  };

  // Show error state
  if (error || showError) {
    const isAccessDenied = error === 'access_denied';
    const message = errorDescription || 
      (isAccessDenied 
        ? 'Azure AD access is pending admin approval. Please use another login method.' 
        : `Authentication failed: ${error}`);
    
    return (
      <PageContainer>
        <div className="flex items-center justify-center min-h-screen">
          <Card className="max-w-md w-full p-8">
            <div className="text-center">
              <div className="mx-auto w-16 h-16 rounded-full bg-yellow-100 dark:bg-yellow-900/30 flex items-center justify-center mb-4">
                <AlertCircle className="h-8 w-8 text-yellow-600 dark:text-yellow-400" />
              </div>
              <h2 className="text-xl font-semibold text-gray-900 dark:text-gray-100 mb-2">
                {isAccessDenied ? 'Access Pending Approval' : 'Authentication Failed'}
              </h2>
              <p className="text-gray-600 dark:text-gray-400 mb-6">
                {message}
              </p>
              {isAccessDenied && (
                <p className="text-sm text-gray-500 dark:text-gray-500 mb-6">
                  The Azure AD application is waiting for administrator approval. 
                  You can use your username and password or Google sign-in instead.
                </p>
              )}
              <Button
                onClick={handleBackToLogin}
                className="w-full"
              >
                <ArrowLeft className="h-4 w-4 mr-2" />
                Back to Login
              </Button>
            </div>
          </Card>
        </div>
      </PageContainer>
    );
  }

  // Show loading state while processing
  return (
    <PageContainer>
      <div className="flex items-center justify-center min-h-screen">
        <Card className="max-w-md w-full p-8">
          <div className="text-center">
            <div className="mx-auto w-16 h-16 rounded-full bg-blue-100 dark:bg-blue-900/30 flex items-center justify-center mb-4">
              <Loader className="h-8 w-8 text-blue-600 dark:text-blue-400 animate-spin" />
            </div>
            <h2 className="text-xl font-semibold text-gray-900 dark:text-gray-100 mb-2">
              Completing Sign In
            </h2>
            <p className="text-gray-600 dark:text-gray-400">
              Please wait while we complete your authentication...
            </p>
          </div>
        </Card>
      </div>
    </PageContainer>
  );
}

